/**
 * Admin Service
 * Handles administrative user management operations
 * Maps to: AdminController.cs
 */
export class AdminService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.basePath = 'api/admin';
    }
    getPath(endpoint) {
        const servicePath = this.config?.servicePath || 'users';
        return `/${servicePath}/${this.basePath}/${endpoint}`;
    }
    // ==================== RAW METHODS ====================
    /**
     * Create user with automatic member creation if IsMember=true (raw)
     */
    async createUserRaw(model) {
        return this.http.post(this.getPath('users'), model);
    }
    /**
     * Get paginated and filtered user list (raw)
     */
    async getUsersRaw(filter) {
        const params = new URLSearchParams();
        if (filter.searchTerm)
            params.append('searchTerm', filter.searchTerm);
        if (filter.roles)
            filter.roles.forEach((r) => params.append('roles', r));
        if (filter.isActive !== undefined)
            if (filter.isActive !== null && filter.isActive !== undefined) {
                params.append('isActive', filter.isActive.toString());
            }
        if (filter.emailConfirmed !== undefined)
            if (filter.emailConfirmed !== null) {
                params.append('emailConfirmed', filter.emailConfirmed.toString());
            }
        if (filter.isLockedOut !== undefined)
            if (filter.isLockedOut !== null) {
                params.append('isLockedOut', filter.isLockedOut.toString());
            }
        if (filter.createdFrom)
            params.append('createdFrom', filter.createdFrom);
        if (filter.createdTo)
            params.append('createdTo', filter.createdTo);
        params.append('page', filter.page.toString());
        params.append('pageSize', filter.pageSize.toString());
        return this.http.get(this.getPath(`users?${params.toString()}`));
    }
    /**
     * Get user by ID (raw)
     */
    async getUserRaw(id) {
        return this.http.get(this.getPath(`users/${id}`));
    }
    /**
     * Update user (raw)
     */
    async updateUserRaw(id, model) {
        return this.http.put(this.getPath(`users/${id}`), model);
    }
    /**
     * Delete user (raw)
     */
    async deleteUserRaw(id) {
        return this.http.delete(this.getPath(`users/${id}`));
    }
    /**
     * Admin reset user password (raw)
     */
    async resetPasswordRaw(id, model) {
        return this.http.post(this.getPath(`users/${id}/reset-password`), model);
    }
    /**
     * Force password expiry for user (raw)
     */
    async forcePasswordExpiryRaw(id, model) {
        return this.http.post(this.getPath(`users/${id}/force-password-expiry`), model);
    }
    /**
     * Assign roles to user (raw)
     */
    async assignRolesRaw(id, model) {
        return this.http.post(this.getPath(`users/${id}/roles`), model);
    }
    /**
     * Get user's roles (raw)
     */
    async getUserRolesRaw(id) {
        return this.http.get(this.getPath(`users/${id}/roles`));
    }
    /**
     * Bulk assign roles to multiple users (raw)
     */
    async bulkAssignRolesRaw(model) {
        return this.http.post(this.getPath('users/bulk-assign-roles'), model);
    }
    /**
     * Activate or deactivate user (raw)
     */
    async toggleUserActivationRaw(id, model) {
        return this.http.post(this.getPath(`users/${id}/toggle-activation`), model);
    }
    /**
     * Import users from CSV (raw)
     */
    async bulkImportUsersRaw(model) {
        return this.http.post(this.getPath('users/bulk-import'), model);
    }
    /**
     * Export users to CSV or Excel (raw)
     */
    async exportUsersRaw(model) {
        return this.http.post(this.getPath('users/export'), model);
    }
    /**
     * Get user activity and audit logs (raw)
     */
    async getUserActivityRaw(id) {
        return this.http.get(this.getPath(`users/${id}/activity`));
    }
    /**
     * Impersonate user (raw)
     */
    async impersonateUserRaw(id, model) {
        return this.http.post(this.getPath(`users/${id}/impersonate`), model);
    }
    /**
     * Get all roles (raw)
     */
    async getRolesRaw() {
        return this.http.get(this.getPath('roles'));
    }
    /**
     * Create role (raw)
     */
    async createRoleRaw(name, description) {
        return this.http.post(this.getPath('roles'), {
            name,
            description,
        });
    }
    /**
     * Get role by ID (raw)
     */
    async getRoleRaw(id) {
        return this.http.get(this.getPath(`roles/${id}`));
    }
    // ==================== CLEAN METHODS ====================
    /**
     * Create user with automatic member creation if IsMember=true
     */
    async createUser(model) {
        const response = await this.createUserRaw(model);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'User creation failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Get paginated and filtered user list
     */
    async getUsers(filter) {
        const response = await this.getUsersRaw(filter);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to fetch users');
        }
        return response.data;
    }
    /**
     * Get user by ID
     */
    async getUser(id) {
        const response = await this.getUserRaw(id);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'User not found');
        }
        return response.data;
    }
    /**
     * Update user
     */
    async updateUser(id, model) {
        const response = await this.updateUserRaw(id, model);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'User update failed');
        }
        return response.data;
    }
    /**
     * Delete user
     */
    async deleteUser(id) {
        const response = await this.deleteUserRaw(id);
        if (!response.success || response.data === undefined) {
            throw new Error(response.message || 'User deletion failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Admin reset user password
     */
    async resetPassword(id, model) {
        const response = await this.resetPasswordRaw(id, model);
        if (!response.success || response.data === undefined) {
            throw new Error(response.message || 'Password reset failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Force password expiry for user
     */
    async forcePasswordExpiry(id, model) {
        const response = await this.forcePasswordExpiryRaw(id, model);
        if (!response.success || response.data === undefined) {
            throw new Error(response.message || 'Force password expiry failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Assign roles to user
     */
    async assignRoles(id, model) {
        const response = await this.assignRolesRaw(id, model);
        if (!response.success || response.data === undefined) {
            throw new Error(response.message || 'Role assignment failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Get user's roles
     */
    async getUserRoles(id) {
        const response = await this.getUserRolesRaw(id);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to fetch user roles');
        }
        return response.data;
    }
    /**
     * Bulk assign roles to multiple users
     */
    async bulkAssignRoles(model) {
        const response = await this.bulkAssignRolesRaw(model);
        if (!response.success || response.data === undefined) {
            throw new Error(response.message || 'Bulk role assignment failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Activate or deactivate user
     */
    async toggleUserActivation(id, model) {
        const response = await this.toggleUserActivationRaw(id, model);
        if (!response.success || response.data === undefined) {
            throw new Error(response.message || 'User activation toggle failed');
        }
        if (response.data === null) {
            throw new Error('Unexpected null value in response data');
        }
        return response.data;
    }
    /**
     * Import users from CSV
     */
    async bulkImportUsers(model) {
        const response = await this.bulkImportUsersRaw(model);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Bulk import failed');
        }
        return response.data;
    }
    /**
     * Export users to CSV or Excel
     */
    async exportUsers(model) {
        const response = await this.exportUsersRaw(model);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'User export failed');
        }
        return response.data;
    }
    /**
     * Get user activity and audit logs
     */
    async getUserActivity(id) {
        const response = await this.getUserActivityRaw(id);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to fetch user activity');
        }
        return response.data;
    }
    /**
     * Impersonate user
     */
    async impersonateUser(id, model) {
        const response = await this.impersonateUserRaw(id, model);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'User impersonation failed');
        }
        return response.data;
    }
    /**
     * Get all roles
     */
    async getRoles() {
        const response = await this.getRolesRaw();
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to fetch roles');
        }
        return response.data;
    }
    /**
     * Create role
     */
    async createRole(name, description) {
        const response = await this.createRoleRaw(name, description);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Role creation failed');
        }
        return response.data;
    }
    /**
     * Get role by ID
     */
    async getRole(id) {
        const response = await this.getRoleRaw(id);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Role not found');
        }
        return response.data;
    }
}
//# sourceMappingURL=AdminService.js.map